import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Portal,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { useDebounce } from '@/client/hooks/useDebounce';
import { useMasterDataList } from '@/lib/sdk/master-data/queries';

export interface ReferenceSearchInputProps {
  id?: string;
  orgId: string;
  /** Dataset to search against (the field definition's configured reference_dataset). */
  dataset: string;
  /** The referenced row's key or label (per valueField), or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  error?: string;
  /** Which field of the picked row `value`/`onChange` carry. Defaults to 'key'. */
  valueField?: 'key' | 'label';
}

/** Debounced search-and-pick input for a value that lives in another master data dataset. */
export function ReferenceSearchInput({
  id,
  orgId,
  dataset,
  value,
  onChange,
  onKeyDown,
  placeholder = 'Search…',
  size = 'sm',
  error,
  valueField = 'key',
}: ReferenceSearchInputProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [listPos, setListPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // The suggestion list is portaled to <body> (see below) so it isn't clipped by
  // the scrollable table/drawer it sits inside, so outside-click detection has to
  // check both the input's container and the portaled list itself.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  // Recompute the portaled list's position whenever it opens, and keep it pinned
  // to the input while the table/drawer scrolls or the window resizes.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setListPos({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const debouncedQuery = useDebounce(query, 250);

  // When the stored value is already the label (valueField === 'label') it's
  // directly displayable — only a key needs a lookup to show its row's label.
  const { data: resolveResult } = useMasterDataList(orgId, {
    dataset,
    search: value,
    page_size: 5,
  }, { enabled: valueField === 'key' && !!value });
  useEffect(() => {
    if (valueField !== 'key' || !value) { setResolvedLabel(null); return; }
    const match = resolveResult?.items.find((r) => r.key === value);
    if (match) setResolvedLabel(match.label);
  }, [value, valueField, resolveResult]);

  const { data: searchResult, isFetching } = useMasterDataList(orgId, {
    dataset,
    search: debouncedQuery,
    page_size: 10,
  });
  const results = isOpen && debouncedQuery.trim() ? (searchResult?.items ?? []) : [];

  const displayValue = isOpen ? query : (valueField === 'label' ? value : (resolvedLabel ?? value));

  return (
    <Box ref={containerRef} position="relative" w="full">
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <InputGroup size={size}>
          <Input
            id={id}
            value={displayValue}
            onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
            onFocus={() => { setQuery(''); setIsOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setIsOpen(false); return; }
              onKeyDown?.(e);
            }}
            placeholder={placeholder}
            variant="unstyled"
            bg="transparent"
            px={2}
            py={1}
            h="32px"
            borderRadius="none"
            borderWidth="1px"
            borderColor={error ? 'red.400' : 'border-default'}
            _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
            _focus={{ borderColor: 'brand.500', outline: 'none' }}
            autoComplete="off"
          />
          {isFetching && isOpen && (
            <InputRightElement h="32px" w="28px">
              <Spinner size="xs" color="brand.500" />
            </InputRightElement>
          )}
          {!isFetching && !isOpen && value && (
            <InputRightElement h="32px" w="28px">
              <Box
                as="button"
                type="button"
                fontSize="sm"
                color="text-secondary"
                _hover={{ color: 'red.500' }}
                onClick={() => { onChange(''); setResolvedLabel(null); setQuery(''); }}
                aria-label="Clear"
              >
                ×
              </Box>
            </InputRightElement>
          )}
        </InputGroup>
      </Tooltip>
      {isOpen && debouncedQuery.trim() && listPos && (
        <Portal>
          <List
            ref={listRef}
            position="fixed"
            top={`${listPos.top + 4}px`}
            left={`${listPos.left}px`}
            width={`${listPos.width}px`}
            zIndex="popover"
            bg="bg-surface"
            borderWidth="1px"
            borderColor="border-default"
            borderRadius="md"
            boxShadow="md"
            maxH="200px"
            overflowY="auto"
          >
            {results.length === 0 && !isFetching && (
              <ListItem px={3} py={2} fontSize="sm" color="text-secondary">No matches in {dataset}</ListItem>
            )}
            {results.map((r) => (
              <ListItem
                key={r.id}
                px={3}
                py={2}
                fontSize="sm"
                cursor="pointer"
                _hover={{ bg: 'interactive-hover' }}
                onClick={() => {
                  onChange(valueField === 'label' ? r.label : r.key);
                  setResolvedLabel(r.label);
                  setQuery('');
                  setIsOpen(false);
                }}
              >
                <Text color="text-primary">{r.label}</Text>
                <Text fontSize="xs" color="text-secondary">{r.key}</Text>
              </ListItem>
            ))}
          </List>
        </Portal>
      )}
    </Box>
  );
}
